import config from '../config';
import { computeCanonicalHash } from '../utils/hash';

/**
 * Helper function to extract in_rxcui values from medication objects (Phase F)
 * @param {Array} medications - Array of medication objects
 * @returns {Array} - Array of in_rxcui integers
 */
export const extractInRxcuis = (medications) => {
  if (!Array.isArray(medications)) {
    return [];
  }
  
  return medications
    .map(med => {
      // Try different possible locations for in_rxcui
      return med?.identifiers?.in_rxcui || 
             med?.in_rxcui || 
             med?.identifiers?.rxcui ||  // Fallback to rxcui if in_rxcui not available
             med?.rxcui;
    })
    .filter(rxcui => rxcui && (typeof rxcui === 'number' || (typeof rxcui === 'string' && !isNaN(parseInt(rxcui)))))
    .map(rxcui => typeof rxcui === 'string' ? parseInt(rxcui) : rxcui);
};

/**
 * Helper function to extract ingredient data (in_rxcui + ingredient_name) from medication objects (Phase F.1)
 * Requires medications to have Phase F.1 format with in_rxcui and ingredient_name
 * @param {Array} medications - Array of medication objects with Phase F.1 data
 * @returns {Array} - Array of {in_rxcui: number, ingredient_name: string} objects
 */
export const extractMedicationIngredients = (medications) => {
  console.log('[extractMedicationIngredients] Input medications:', medications);
  
  if (!Array.isArray(medications)) {
    console.log('[extractMedicationIngredients] Input is not an array');
    return [];
  }
  
  const results = medications
    .map((med, index) => {
      console.log(`[extractMedicationIngredients] Processing medication ${index}:`, med);
      
      // Extract in_rxcui - Phase F.1 only
      const in_rxcui = med?.identifiers?.in_rxcui || med?.in_rxcui;
                       
      // Extract ingredient_name - Phase F.1 only
      const ingredient_name = med?.identifiers?.ingredient_name || med?.ingredient_name;
      
      if (!in_rxcui || !ingredient_name) {
        console.warn(`[extractMedicationIngredients] Medication ${index} missing Phase F.1 data:`, {
          in_rxcui,
          ingredient_name,
          medication: med
        });
        return null;
      }
      
      console.log(`[extractMedicationIngredients] Extracted for medication ${index}:`, {
        in_rxcui,
        ingredient_name,
        originalMed: {
          display: med?.name?.display,
          identifiers: med?.identifiers
        }
      });
      
      return {
        in_rxcui: typeof in_rxcui === 'string' ? parseInt(in_rxcui) : in_rxcui,
        ingredient_name: ingredient_name
      };
    })
    .filter((med, index) => {
      if (med === null) return false;
      const isValid = med.in_rxcui && !isNaN(med.in_rxcui) && med.ingredient_name;
      console.log(`[extractMedicationIngredients] Medication ${index} valid:`, isValid, med);
      return isValid;
    });
  
  console.log('[extractMedicationIngredients] Final results:', results);
  return results;
};

/**
 * Search for medications using the backend API
 * @param {string} searchTerm - The medication name to search for
 * @returns {Promise<Array>} - Array of medication suggestions
 */
export const searchMedications = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  try {
    const response = await fetch(
      `${config.BASE_URL}/medications/search?term=${encodeURIComponent(searchTerm)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error searching medications:', error);
    throw error;
  }
};

/**
 * Get detailed information for a specific medication by RxCUI
 * @param {string} rxcui - The RxCUI identifier for the medication
 * @returns {Promise<Object>} - Detailed medication information
 */
export const getMedicationDetails = async (rxcui) => {
  if (!rxcui) {
    throw new Error('RxCUI is required');
  }

  try {
    const response = await fetch(
      `${config.BASE_URL}/medications/rxcui/${encodeURIComponent(rxcui)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching medication details:', error);
    throw error;
  }
};

// ─── Logged-out visitors (STACK-565) ────────────────────────────────────────
// What a logged-out visitor types and picks travels only in request bodies:
// Cloud Run's request log keeps every URL beside the caller's IP
// (docs/guest-medication-check.md §3.1). These three never put medication
// data in a URL, and never send an auth header.

/** An HTTP failure that keeps its status and the body's `error` code. */
export class GuestRequestError extends Error {
  constructor(status, code) {
    super(code || `HTTP ${status}`);
    this.name = 'GuestRequestError';
    this.status = status;
    this.code = code || null;
  }
}

/**
 * POSTs JSON and reads the JSON answer, if any. `headers` adds to the content
 * type — the interaction checker page passes a member's Authorization there.
 */
export const postJson = async (path, body, headers = {}) => {
  const response = await fetch(`${config.BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }
  return { response, data };
};

/** POST /medications/search — the guest picker's search. */
export const searchMedicationsGuest = async (term) => {
  if (!term || term.trim().length < 2) return [];
  const { response, data } = await postJson('/medications/search', { term: term.trim() });
  if (!response.ok) throw new GuestRequestError(response.status, data?.error);
  return Array.isArray(data) ? data : [];
};

/** POST /medications/details — resolves a picked product to its ingredient. */
export const getMedicationDetailsGuest = async (rxcui) => {
  const { response, data } = await postJson('/medications/details', { rxcui: String(rxcui) });
  if (!response.ok) throw new GuestRequestError(response.status, data?.error);
  return data;
};

/**
 * POST /check-interactions-guest. Resolves with the members' response shape
 * (plus `isProcessing`) on 200 and 202; every refusal (400, 409, 429, 503,
 * other 5xx) throws a GuestRequestError carrying its status and code.
 *
 * `healthGoals` must be the exact string sent to /v2/generate-stack: the
 * server finds the stack by it.
 */
export const checkGuestInteractions = async (healthGoals, rxcuis) => {
  const answer = await postJson('/check-interactions-guest', {
    healthGoals,
    medications: rxcuis,
  });
  return interactionCheckAnswer(answer);
};

/**
 * A public interaction check's answer: the members' response shape plus
 * `isProcessing` on 200 and 202; any other status throws a GuestRequestError
 * carrying it and the body's `error` code. Shared with the interaction
 * checker page's route (STACK-587).
 */
export const interactionCheckAnswer = ({ response, data }) => {
  if (response.status === 200 || response.status === 202) {
    return {
      ...(data || {}),
      isProcessing: response.status === 202,
      statusCode: response.status,
    };
  }
  throw new GuestRequestError(response.status, data?.error);
};

/**
 * Check for interactions between medications and supplements
 *
 * @param {Array} medicationIngredients - Array of medication ingredient objects with {in_rxcui, ingredient_name}
 * @param {Array} userSupplements - Array of supplement keys from user's stacks
 * @param {string} authToken - Firebase Auth token
 * @returns {Promise<Object>} - Interaction results
 */
export const checkInteractions = async (medicationIngredients, userSupplements, authToken = null) => {
  if (!medicationIngredients || !userSupplements || medicationIngredients.length === 0 || userSupplements.length === 0) {
    return { interactions: [], status: 'no_items_to_check' };
  }

  // Validate medication ingredients format
  for (const med of medicationIngredients) {
    if (!med.in_rxcui || !med.ingredient_name) {
      throw new Error('Invalid medication ingredient format: requires in_rxcui and ingredient_name');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Phase F.1 format only
  const requestBody = {
    user_med_ingredients: medicationIngredients,
    user_supplements: userSupplements
  };

  try {
    const response = await fetch(
      `${config.BASE_URL}/check-interactions`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      }
    );

    // Handle both 200 (completed) and 202 (processing) as success
    if (response.ok || response.status === 202) {
      const data = await response.json();
      
      // Add status information for processing cases
      if (response.status === 202) {
        data.isProcessing = true;
        data.statusCode = 202;
      } else {
        data.isProcessing = false;
        data.statusCode = 200;
      }
      
      return data;
    }

    // Handle other error cases
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);

  } catch (error) {
    console.error('Error checking interactions:', error);
    throw error;
  }
};

/**
 * Generate a medication support stack based on the user's current medication list
 * This function calls the backend to analyze the user's medications and recommend
 * supplements to counteract potential nutrient depletions.
 * 
 * @param {string} authToken - Firebase Auth token (required)
 * @returns {Promise<Object>} - Generated stack data or confirmation message
 */
export const generateMedicationSupportStack = async (authToken, medications) => {
  if (!authToken) {
    throw new Error('Authentication token is required for generating medication support stack');
  }

  if (!medications || !Array.isArray(medications)) {
    console.warn('Medication list is missing or not an array. Skipping support stack generation.');
    return; // Or throw an error if this should be a blocking issue
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`
  };

  // Compute meds_hash (latest-wins gate)
  const meds_hash = await computeCanonicalHash(medications || []);
  const body = JSON.stringify({ medications, meds_hash });

  try {
    const response = await fetch(
      `${config.BASE_URL}/medications/generate-support-stack`,
      {
        method: 'POST',
        headers,
        body,
      }
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    // Handle error cases
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);

  } catch (error) {
    console.error('Error generating medication support stack:', error);
    throw error;
  }
}; 